import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtLogCompact } from "@/lib/tz";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Bug, Lightbulb, Loader2, Search, Plus, Send, Sparkles, Copy, Paperclip, ShieldAlert, Upload, Newspaper, GitMerge, ArrowLeftRight,
} from "lucide-react";
import { ImportIssuesDialog } from "@/components/ImportIssuesDialog";
import { RichTextEditor, sanitizeHtml, isEmptyHtml } from "@/components/RichTextEditor";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { toast } from "sonner";
import { IssueTrackerAiConfig } from "@/components/IssueTrackerAiConfig";
import { ReleaseNotesDialog } from "@/components/ReleaseNotesDialog";
import { useAuth } from "@/context/AuthContext";


const CRIMSON = "#bf3b4f";
const STATUSES = ["New", "In Progress", "Pending", "Waiting to be Deployed", "Resolved"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const PRIORITY_STYLE = {
  Critical: "bg-red-100 text-red-800 border-red-300",
  High: "bg-orange-50 text-orange-700 border-orange-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_STYLE = {
  New: "bg-sky-50 text-sky-700 border-sky-200",
  "In Progress": "bg-violet-50 text-violet-700 border-violet-200",
  Pending: "bg-orange-50 text-orange-700 border-orange-200",
  "Waiting to be Deployed": "bg-amber-50 text-amber-700 border-amber-200",
  Resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function IssueTracker() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("open");
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dupeReview, setDupeReview] = useState(null);
  const [picked, setPicked] = useState([]);   // up to two issue ids ticked for a manual merge
  const [aiConfigOpen, setAiConfigOpen] = useState(false);
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);

  const { can } = useAuth();

  const load = () => api.issues({
    status: status === "all" ? undefined : status,
    type: type === "all" ? undefined : type,
    q: q.trim() || undefined,
  }).then(setData).catch(() => { setData({ issues: [], counts: {} }); toast.error("Could not load the issue tracker."); });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, type]);

  const rows = data?.issues || [];

  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id].slice(-2)));

  // Keep the older ticket by default (or the open one, if only one is still open).
  const startManualMerge = () => {
    const [a, b] = picked.map((id) => rows.find((r) => r.issue_id === id)).filter(Boolean);
    if (!a || !b) return;
    let [keep, close] = (a.reported_at || "") <= (b.reported_at || "") ? [a, b] : [b, a];
    if (keep.status === "Resolved" && close.status !== "Resolved") [keep, close] = [close, keep];
    setDupeReview({ dupId: close.issue_id, canonId: keep.issue_id, manual: true });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="issues-page">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="flex items-center gap-2.5">
            <ShieldAlert className="h-5 w-5" style={{ color: CRIMSON }} />
            <h1 className="text-xl font-semibold font-brand text-slate-800">Issue Tracker</h1>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Everything reported through the Feedback button, plus anything you log yourself.
            Sorted by priority, then longest waiting first.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setAiConfigOpen(true)} data-testid="ai-settings-button">AI Settings</Button>
          {can.has("system.issue_tracker") && (
            <Button variant="outline" size="sm" onClick={() => setReleaseNotesOpen(true)} data-testid="release-notes-button" className="gap-1.5">
              <Newspaper className="h-3.5 w-3.5" />Release Notes
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="issue-import">Import</Button>
          <Button variant="outline" size="sm" onClick={() => setLogOpen(true)} data-testid="log-issue">Log Issue</Button>
        </div>
      </div>

      {/* AI Settings Dialog */}
      <Dialog open={aiConfigOpen} onOpenChange={setAiConfigOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-settings-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
            <DialogTitle className="text-base font-brand flex items-center gap-2">
              <Sparkles className="h-4 w-4" style={{ color: CRIMSON }} />AI Triage Settings
            </DialogTitle>
          </DialogHeader>
          <div className="p-5">
            <IssueTrackerAiConfig />
          </div>
        </DialogContent>
      </Dialog>


      <div className="flex flex-wrap gap-1.5 my-3">
        {STATUSES.map((s) => (
          <Badge key={s} className={`text-[10px] py-0.5 border ${STATUS_STYLE[s]}`}>
            {s} · {data?.counts?.[s] ?? 0}
          </Badge>
        ))}
      </div>

      {picked.length > 0 && (
        <div className="flex items-center gap-2 mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="merge-bar">
          <span className="text-xs text-slate-600">
            {picked.length === 1 ? "Tick one more issue to merge them." : `${picked.join(" and ")} selected.`}
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPicked([])}>Clear</Button>
            <Button size="sm" style={{ backgroundColor: CRIMSON }} className="gap-1.5" disabled={picked.length !== 2}
              onClick={startManualMerge} data-testid="merge-selected-btn">
              <GitMerge className="h-3.5 w-3.5" />Merge selected
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            className="h-9 pl-8 text-sm" placeholder="Search id, reporter or description" data-testid="issue-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-48 text-xs" data-testid="issue-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open" className="text-xs">Open issues</SelectItem>
            <SelectItem value="all" className="text-xs">All issues</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-9 w-40 text-xs" data-testid="issue-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">Bugs & requests</SelectItem>
            <SelectItem value="Bug" className="text-xs">Bugs</SelectItem>
            <SelectItem value="Feature Request" className="text-xs">Feature requests</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {data === null && <div className="text-xs text-slate-400 py-10 text-center">Loading…</div>}
      {data && rows.length === 0 && <div className="text-xs text-slate-400 py-10 text-center" data-testid="issues-empty">Nothing here yet.</div>}

      <div className="space-y-2">
        {rows.map((i) => (
          <div key={i.issue_id} className="flex items-stretch gap-2">
            <label className="flex items-center px-1 cursor-pointer" title="Select to merge">
              <Checkbox checked={picked.includes(i.issue_id)} onCheckedChange={() => togglePick(i.issue_id)}
                disabled={!!i.merged_into} data-testid={`issue-pick-${i.issue_id}`} />
            </label>
          <button onClick={() => setSel(i.issue_id)} data-testid={`issue-row-${i.issue_id}`}
            className="flex-1 min-w-0 text-left rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 hover:border-slate-300 transition-colors">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-mono text-slate-400">{i.issue_id}</span>
              {i.type === "Bug"
                ? <Badge className="text-[9px] py-0 bg-rose-50 text-[#bf3b4f] border border-rose-200"><Bug className="h-2.5 w-2.5 mr-0.5" />Bug</Badge>
                : <Badge className="text-[9px] py-0 bg-indigo-50 text-indigo-700 border border-indigo-200"><Lightbulb className="h-2.5 w-2.5 mr-0.5" />Request</Badge>}
              <Badge className={`text-[9px] py-0 border ${PRIORITY_STYLE[i.priority] || ""}`}>{i.priority}</Badge>
              <Badge className={`text-[9px] py-0 border ${STATUS_STYLE[i.status] || ""}`}>{i.status}</Badge>
              {i.attachment_count > 0 && (
                <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Paperclip className="h-2.5 w-2.5" />{i.attachment_count}</span>
              )}
              {i.ai?.duplicate_of?.length > 0 && (
                <Badge className="text-[9px] py-0 bg-amber-50 text-amber-700 border border-amber-200">Possible duplicate</Badge>
              )}
            </div>
            <div className="text-xs font-medium text-slate-800 mt-1">
              {i.ai?.title || i.what_happened || i.description}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              {i.reporter_name} ({i.reporter_role}) · {fmtLogCompact(i.reported_at)}
              {i.context?.path ? ` · ${i.context.path}` : ""}
            </div>
          </button>
          </div>
        ))}
      </div>

      {sel && <ErrorBoundary><IssueDetail id={sel} onClose={() => setSel(null)} onChanged={load} setDupeReview={setDupeReview} /></ErrorBoundary>}
      <ErrorBoundary><LogIssueDialog open={logOpen} onOpenChange={setLogOpen} onLogged={load} /></ErrorBoundary>
      <ImportIssuesDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
      <ReleaseNotesDialog open={releaseNotesOpen} onOpenChange={setReleaseNotesOpen} />
      <DuplicateReviewDialog open={!!dupeReview} onOpenChange={(v) => { if (!v) setDupeReview(null); }} dupId={dupeReview?.dupId} canonId={dupeReview?.canonId} manual={dupeReview?.manual}
        onSwap={() => setDupeReview((d) => ({ ...d, dupId: d.canonId, canonId: d.dupId }))}
        onMerged={() => { setSel(null); setPicked([]); load(); }} />

    </div>
  );
}

function IssueDetail({ id, onClose, onChanged, setDupeReview }) {
  const [issue, setIssue] = useState(null);
  const [shots, setShots] = useState({});
  const [comment, setComment] = useState("");
  const [notify, setNotify] = useState(true);
  const [addToContext, setAddToContext] = useState(false);
  const [commentFiles, setCommentFiles] = useState([]);
  const [commentAttachUrls, setCommentAttachUrls] = useState({});
  const [resolution, setResolution] = useState("");
  const [busy, setBusy] = useState(false);
  const [retriaging, setRetriaging] = useState(false);

  const load = () => api.issue(id).then((d) => {
    setIssue(d);
    setResolution(d.resolution?.note || "");
    (d.attachments || []).forEach((a) => {
      api.issueAttachmentUrl(id, a.id).then((url) => setShots((s) => ({ ...s, [a.id]: url }))).catch(() => {});
    });
    // Load comment attachment thumbnails for all comments
    const urls = {};
    (d.comments || []).forEach((c, ci) => {
      (c.attachments || []).forEach((a) => {
        api.issueCommentAttachmentUrl(id, ci, a.id)
          .then((url) => setCommentAttachUrls((s) => ({ ...s, [`${ci}:${a.id}`]: url })))
          .catch(() => {});
      });
    });
    return urls;
  }).catch(() => toast.error("Could not open that issue."));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = async (body) => {
    setBusy(true);
    try { await api.patchIssue(id, body); await load(); onChanged(); toast.success("Issue updated."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update the issue."); }
    finally { setBusy(false); }
  };

  const postComment = async () => {
    if (isEmptyHtml(comment)) return;
    setBusy(true);
    try {
      await api.commentIssue(id, { text: comment, notify, add_to_context: addToContext }, commentFiles);
      setComment(""); 
      setAddToContext(false);
      setCommentFiles([]);
      await load();
      toast.success(notify ? "Update sent to the reporter." : "Note added.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add the comment."); }
    finally { setBusy(false); }
  };

  const retriage = async () => {
    setRetriaging(true);
    try {
      const result = await api.retriageIssue(id);
      await load();
      if (!result.triaged) toast.error("The AI analysis couldn't run. Try again shortly.");
      else toast.success(`AI analysis complete${result.context_comments_used ? ` with ${result.context_comments_used} additional comment${result.context_comments_used !== 1 ? "s" : ""}` : ""}.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not re-run AI analysis."); }
    finally { setRetriaging(false); }
  };

  const bug = issue?.type === "Bug";
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] flex flex-col sm:max-w-3xl" data-testid="issue-detail">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <span className="font-mono text-xs text-slate-400">{id}</span>
            {issue?.ai?.title || (bug ? "Bug report" : "Feature request")}
          </DialogTitle>
        </DialogHeader>


        {!issue ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <div className="overflow-y-auto space-y-4 pr-1">
            <div className="text-[11px] text-slate-500">
              Reported by <b className="text-slate-700">{issue.reporter_name}</b> ({issue.reporter_role})
              {issue.on_behalf_of ? ` on behalf of ${issue.on_behalf_of}` : ""} · {fmtLogCompact(issue.reported_at)}
              {issue.context?.path && <> · screen <span className="font-mono">{issue.context.path}</span></>}
              {issue.context?.viewport && <> · {issue.context.viewport}</>}
            </div>
            {issue.co_reporters?.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {issue.co_reporters.map((cr) => (
                  <div key={cr.email} className="text-[10px] text-slate-500 flex items-center gap-1">
                    <span>+</span>
                    <span className="text-slate-600 font-medium">{cr.name}</span>
                    <span className="text-slate-400">(merged from <span className="font-mono">{cr.merged_from}</span>)</span>
                  </div>
                ))}
              </div>
            )}
            {issue.context?.user_agent && (
              <div className="text-[10px] text-slate-400 -mt-3 truncate">{issue.context.user_agent}</div>
            )}

            <div className="grid sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px]">Type</Label>
                <Select value={issue.type} onValueChange={(v) => patch({ type: v })}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="issue-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bug" className="text-xs">Bug</SelectItem>
                    <SelectItem value="Feature Request" className="text-xs">Feature Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]">Priority {issue.priority_source === "ai" ? "(AI)" : issue.priority_source === "admin" ? "(set by you)" : ""}</Label>
                <Select value={issue.priority} onValueChange={(v) => patch({ priority: v })}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="issue-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]">Status</Label>
                <Select value={issue.status}
                  onValueChange={(v) => v === "Resolved" ? patch({ status: "Resolved", resolution: resolution.trim() }) : patch({ status: v })}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="issue-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              {bug ? (
                <>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">What's happening</div>
                    <div className="text-xs text-slate-800 whitespace-pre-wrap">{issue.what_happened || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Expected behaviour</div>
                    <div className="text-xs text-slate-800 whitespace-pre-wrap">{issue.expected_behaviour || "—"}</div>
                  </div>
                </>
              ) : (
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Request</div>
                  <div className="text-xs text-slate-800 whitespace-pre-wrap">{issue.description || "—"}</div>
                </div>
              )}
            </div>

            {!!(issue.attachments || []).length && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="issue-attachments">
                {issue.attachments.map((a) => (
                  <a key={a.id} href={shots[a.id]} target="_blank" rel="noreferrer"
                    className="rounded-md overflow-hidden border border-slate-200 block" data-testid={`issue-shot-${a.id}`}>
                    {shots[a.id]
                      ? <img src={shots[a.id]} alt={a.filename} className="w-full h-28 object-cover" />
                      : <div className="h-28 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-300" /></div>}
                  </a>
                ))}
              </div>
            )}

            {!issue.ai?.triaged && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-between gap-3" data-testid="issue-ai-missing">
                <div className="text-[11px] text-slate-600">
                  <Sparkles className="h-3.5 w-3.5 inline mr-1 text-violet-600" />
                  {issue.ai?.error ? "The AI analysis didn't work for this report." : "This report hasn't had an AI analysis."}
                </div>
                <Button variant="outline" size="sm" className="h-7 text-[10px] shrink-0" onClick={retriage} disabled={retriaging} data-testid="issue-triage-run">
                  {retriaging ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}Run AI analysis
                </Button>
              </div>
            )}
            {issue.ai?.triaged && (
              <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 space-y-1.5" data-testid="issue-ai">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-violet-800">
                  <Sparkles className="h-3.5 w-3.5" />AI triage
                </div>
                <div className="text-[11px] text-slate-700">{issue.ai.summary}</div>
                <div className="text-[10px] text-slate-500">Priority: {issue.ai.priority} — {issue.ai.priority_reason}</div>
                {!!issue.ai.duplicate_of?.length && (
                  <div className="text-[10px] text-amber-700 flex flex-wrap items-center gap-1">
                    <span>Possible duplicate of</span>
                    {issue.ai.duplicate_of.map((dupId) => (
                      <button
                        key={dupId}
                        onClick={() => setDupeReview({ dupId: issue.issue_id, canonId: dupId })}
                        className="underline font-medium hover:text-amber-900 transition-colors"
                        data-testid={`dupe-link-${dupId}`}
                      >
                        {dupId}
                      </button>
                    ))}
                    <span>— click to review</span>
                  </div>
                )}
                {issue.ai.developer_prompt && (
                  <div className="rounded bg-white border border-violet-200 p-2">
                    <div className="text-[10px] font-semibold text-slate-500 mb-1">Prompt for the developer</div>
                    <div className="text-[11px] text-slate-700 whitespace-pre-wrap">{issue.ai.developer_prompt}</div>
                    <Button variant="outline" size="sm" className="h-6 text-[10px] mt-1.5" data-testid="issue-copy-prompt"
                      onClick={() => { navigator.clipboard.writeText(issue.ai.developer_prompt); toast.success("Prompt copied."); }}>
                      <Copy className="h-3 w-3 mr-1" />Copy
                    </Button>
                  </div>
                )}
              </div>
            )}
            {issue.ai && issue.ai.triaged === false && (
              <div className="text-[10px] text-amber-700" data-testid="issue-ai-failed">
                AI triage didn't run for this one — priority left at Medium.
              </div>
            )}

            <div>
              <Label className="text-xs">Resolution note {issue.status === "Resolved" ? "" : "(required to resolve)"}</Label>
              <RichTextEditor
                value={resolution}
                onChange={setResolution}
                headings
                links
                testid="issue-resolution"
                placeholder="What was done to fix it? The reporter sees this."
              />
              {issue.status !== "Resolved" && (
                <Button size="sm" className="h-8 text-[11px] mt-2" style={{ backgroundColor: CRIMSON }}
                  disabled={busy || isEmptyHtml(resolution)} data-testid="issue-resolve"
                  onClick={() => patch({ status: "Resolved", resolution: resolution })}>
                  Mark resolved
                </Button>
              )}
              {issue.resolution && (
                <div className="text-[10px] text-slate-400 mt-1">
                  Resolved by {issue.resolution.by_name} · {fmtLogCompact(issue.resolution.at)}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Comments</Label>
                {issue.ai?.triaged && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[10px]"
                    onClick={retriage}
                    disabled={retriaging}
                    data-testid="issue-retriage-btn"
                  >
                    {retriaging ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                    Re-run AI Analysis
                  </Button>
                )}
              </div>
              {(issue.comments || []).length === 0 && <div className="text-[11px] text-slate-400">No comments yet.</div>}
              {(issue.comments || []).map((c, i) => (
                <div key={i} className="rounded-md border border-slate-200 px-2.5 py-1.5" data-testid={`issue-comment-${i}`}>
                  <div className="text-[10px] text-slate-400 flex items-center gap-1.5 flex-wrap">
                    <span>{c.by_name} · {fmtLogCompact(c.at)}</span>
                    {c.notify ? (
                      <Badge className="text-[9px] py-0 px-1 bg-sky-50 text-sky-700 border-sky-200">sent to reporter</Badge>
                    ) : (
                      <Badge className="text-[9px] py-0 px-1 bg-slate-50 text-slate-600 border-slate-200">internal note</Badge>
                    )}
                    {c.merged_from && (
                      <Badge className="text-[9px] py-0 px-1 bg-amber-50 text-amber-700 border-amber-200">from {c.merged_from}</Badge>
                    )}
                    {c.add_to_context && (
                      <Badge className="text-[9px] py-0 px-1 bg-purple-50 text-purple-700 border-purple-200" data-testid={`issue-comment-context-${i}`}>
                        <Sparkles className="h-2.5 w-2.5 mr-0.5" />AI context
                      </Badge>
                    )}
                  </div>
                  <div
                    className="text-[11px] text-slate-700 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_h2]:font-semibold [&_h2]:text-xs [&_h3]:font-semibold [&_h3]:text-[11px] [&_a]:underline [&_a]:text-teal-700 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-slate-200 [&_th]:px-1.5 [&_th]:py-0.5 [&_th]:text-left [&_td]:border [&_td]:border-slate-200 [&_td]:px-1.5 [&_td]:py-0.5 [&_p]:mb-1"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(c.text) }}
                  />
                  {(c.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {c.attachments.map((a) => {
                        const url = commentAttachUrls[`${i}:${a.id}`];
                        return url ? (
                          <a key={a.id} href={url} target="_blank" rel="noreferrer"
                            className="block rounded border border-slate-200 overflow-hidden hover:border-slate-400 transition-colors"
                            title={a.filename}>
                            <img src={url} alt={a.filename} className="h-16 w-auto max-w-[120px] object-cover" />
                          </a>
                        ) : (
                          <div key={a.id} className="flex items-center gap-1 text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                            <Paperclip className="h-3 w-3" />{a.filename}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              <RichTextEditor
                value={comment}
                onChange={setComment}
                headings
                links
                testid="issue-comment-box"
                placeholder="Add a comment…"
              />
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1.5">
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                    <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="issue-comment-notify" />
                    Email this to {issue.reporter_name?.split(" ")[0]}
                  </label>
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                    <Checkbox checked={addToContext} onCheckedChange={(v) => setAddToContext(!!v)} data-testid="issue-comment-context" />
                    <span className="flex items-center gap-1">
                      Add to ticket context
                      <span className="text-[10px] text-slate-400">(helps AI understand the issue)</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer" data-testid="issue-comment-attach-label">
                    <Upload className="h-3.5 w-3.5 text-slate-400" />
                    <span className="underline">Attach images</span>
                    <input type="file" accept="image/*" multiple hidden data-testid="issue-comment-files"
                      onChange={(e) => setCommentFiles([...(e.target.files || [])])} />
                  </label>
                  {commentFiles.length > 0 && (
                    <div className="text-[10px] text-slate-400 flex items-center gap-1 flex-wrap">
                      <Paperclip className="h-3 w-3" />
                      {commentFiles.map((f) => f.name).join(", ")}
                      <button className="underline text-slate-500 ml-1" onClick={() => setCommentFiles([])}>clear</button>
                    </div>
                  )}
                </div>
                <Button size="sm" className="h-8 text-[11px]" style={{ backgroundColor: CRIMSON }}
                  disabled={busy || isEmptyHtml(comment)} onClick={postComment} data-testid="issue-comment-send">
                  <Send className="h-3 w-3 mr-1" />Add comment
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="shrink-0 border-t border-slate-100 pt-3">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- keep Dialog gs---

function DuplicateReviewDialog({ open, onOpenChange, dupId, canonId, onMerged, manual, onSwap }) {
  const [dup, setDup]       = useState(null);
  const [canon, setCanon]   = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy]     = useState(false);
  const [reanalyse, setReanalyse] = useState(false);

  useEffect(() => {
    if (!open || !dupId || !canonId) return;
    setDup(null); setCanon(null); setBusy(false); setReanalyse(false);
    Promise.all([api.issue(dupId), api.issue(canonId)]).then(([d, c]) => {
      setDup(d);
      setCanon(c);
      setPrompt(c.ai?.developer_prompt || "");
    }).catch(() => toast.error("Could not load issue details."));
  }, [open, dupId, canonId]);

  const handleMerge = async () => {
    setBusy(true);
    try {
      await api.mergeIssue(dupId, { into: canonId, developer_prompt: prompt || undefined });
      toast.success(`${dupId} merged into ${canonId}.`);
      if (reanalyse) {
        try {
          await api.retriageIssue(canonId);
          toast.success(`AI analysis re-run on ${canonId} with the merged-in details.`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || `Merged, but the AI analysis could not be re-run. Use Re-run AI Analysis on ${canonId}.`);
        }
      }
      onMerged();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed.");
      setBusy(false);
    }
  };

  const handleNotDuplicate = async () => {
    setBusy(true);
    try {
      await api.patchIssue(dupId, { clear_duplicate_of: true });
      toast.success("Duplicate suggestion dismissed.");
      onMerged();
      onOpenChange(false);
    } catch {
      toast.error("Could not dismiss suggestion.");
      setBusy(false);
    }
  };

  const loading = !dup || !canon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} data-testid="dupe-review-dialog">
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-base">{manual ? "Merge issues" : "Review possible duplicate"}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="overflow-y-auto space-y-4 pr-1">
            {/* Side-by-side comparison */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "This issue (will be closed)", issue: dup },
                { label: `Canonical issue (will be kept)` , issue: canon },
              ].map(({ label, issue }) => (
                <div key={issue.issue_id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1.5">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-[10px] font-mono text-slate-400">{issue.issue_id}</span>
                    <Badge className={`text-[9px] py-0 border ${PRIORITY_STYLE[issue.priority] || ""}`}>{issue.priority}</Badge>
                    <Badge className={`text-[9px] py-0 border ${STATUS_STYLE[issue.status]  || ""}`}>{issue.status} </Badge>
                  </div>
                  <div className="text-xs font-medium text-slate-800">
                    {issue.ai?.title || issue.what_happened || issue.description || "—"}
                  </div>
                  {issue.ai?.summary && <div className="text-[11px] text-slate-600">{issue.ai.summary}</div>}
                  <div className="text-[10px] text-slate-400 ">
                    Reported by {issue.reporter_name} {issue.reporter_role ? `· ${issue.reporter_role}` : ""}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-start justify-between gap-3 text-[11px] text-slate-600">
              <p>
                Everything on {dupId} moves to {canonId}: its original report, comments and screenshots.
                {" "}{dup.reporter_name} and {canon.reporter_name} will both get future updates on {canonId}, and
                {" "}{dupId} is closed as merged.
              </p>
              {manual && onSwap && (
                <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={onSwap} disabled={busy} data-testid="merge-swap-btn">
                  <ArrowLeftRight className="h-3.5 w-3.5" />Keep {dupId} instead
                </Button>
              )}
            </div>

            {/* Developer prompt editor */}
            <div>
              <Label className="text-[10px]">
                Developer prompt for {canonId}
                <span className="font-normal text-slate-400 ml-1"> (edit to incorporate both reports before merging)</span>
              </Label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={5}
                className="mt-1 text-sm font-mono"
                placeholder="No developer prompt yet — you can add one here."
                data-testid="dupe-prompt-editor"
              />
            </div>

            <label className="flex items-start gap-2 text-[11px] text-slate-600 cursor-pointer">
              <Checkbox checked={reanalyse} onCheckedChange={(v) => setReanalyse(!!v)} className="mt-0.5" data-testid="merge-reanalyse" />
              <span>
                Re-run AI analysis on {canonId} after merging, using the merged-in report, comments and screenshots.
                <span className="text-slate-400"> This regenerates the title, summary and developer prompt, replacing the prompt above.</span>
              </span>
            </label>
          </div>
        )}

        <DialogFooter className="shrink-0 border-t border-slate-100 pt-3 flex justify-between">
          {manual ? <span /> : (
            <Button variant="outline" size="sm" onClick={handleNotDuplicate} disabled={busy || loading} data-testid="dupe-not-duplicate">
              Not a duplicate
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={handleMerge} disabled={busy || loading} data-testid="dupe-merge-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Merge {dupId} into {canonId}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LogIssueDialog({ open, onOpenChange, onLogged }) {
  const [type, setType] = useState("Bug");
  const [what, setWhat] = useState("");
  const [expected, setExpected] = useState("");
  const [desc, setDesc] = useState("");
  const [behalf, setBehalf] = useState("");
  const [priority, setPriority] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const canSave = useMemo(() => (type === "Bug" ? what.trim() : desc.trim()).length > 2, [type, what, desc]);

  const save = async () => {
    setBusy(true);
    try {
      const { issue_id } = await api.logIssue({
        type, on_behalf_of: behalf.trim() || null, priority: priority || null,
        description: type === "Feature Request" ? desc.trim() : "",
        what_happened: type === "Bug" ? what.trim() : "",
        expected_behaviour: type === "Bug" ? expected.trim() : "",
        context: { path: "Logged by admin", app: "Prydes Support web" },
      });
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f, f.name);
        fd.append("kind", "upload");
        await api.uploadFeedbackAttachment(issue_id, fd);
      }
      api.triageFeedback(issue_id).catch(() => {});
      toast.success(`Logged as ${issue_id}.`);
      setType("Bug"); setWhat(""); setExpected(""); setDesc(""); setBehalf(""); setPriority(""); setFiles([]);
      onOpenChange(false); onLogged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log that issue.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] flex flex-col sm:max-w-xl" data-testid="log-issue-dialog">
        <DialogHeader><DialogTitle className="text-base">Log an issue</DialogTitle></DialogHeader>
        <div className="overflow-y-auto space-y-3 pr-1">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="log-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bug" className="text-xs">Bug</SelectItem>
                  <SelectItem value="Feature Request" className="text-xs">Feature Request</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px]">Priority (optional — AI sets it otherwise)</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="log-priority"><SelectValue placeholder="Let the AI decide" /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[10px]">Raised by (optional)</Label>
            <Input value={behalf} onChange={(e) => setBehalf(e.target.value)} className="h-8 text-sm mt-1"
              placeholder="e.g. Mickie — mentioned on the phone" data-testid="log-behalf" />
          </div>
          {type === "Bug" ? (
            <>
              <div>
                <Label className="text-xs">What's happening?</Label>
                <Textarea value={what} onChange={(e) => setWhat(e.target.value)} rows={3} className="mt-1 text-sm" data-testid="log-what" />
              </div>
              <div>
                <Label className="text-xs">Expected behaviour</Label>
                <Textarea value={expected} onChange={(e) => setExpected(e.target.value)} rows={2} className="mt-1 text-sm" data-testid="log-expected" />
              </div>
            </>
          ) : (
            <div>
              <Label className="text-xs">What should it do?</Label>
              <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} className="mt-1 text-sm" data-testid="log-description" />
            </div>
          )}
          <div>
            <Label className="text-[10px]">Attachments</Label>
            <label className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
              <Upload className="h-3.5 w-3.5" />
              <span className="underline">Choose images</span>
              <input type="file" accept="image/*" multiple hidden data-testid="log-files"
                onChange={(e) => setFiles([...(e.target.files || [])])} />
            </label>
            {!!files.length && <div className="text-[10px] text-slate-400 mt-1">{files.map((f) => f.name).join(", ")}</div>}
          </div>
        </div>
        <DialogFooter className="shrink-0 border-t border-slate-100 pt-3">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={save} disabled={busy || !canSave} data-testid="log-save">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}Log issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
